import {
  ImportedTicket_Read,
  postV1CompaniesCompanyIdImportsTickets,
  TicketImportRequest,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { CSVColumn } from '~utils/csv/csvProcessor';

import { trimmedOrUndefined, withoutEmptyValues } from './importPayload';
import { EntityCSVConfig } from './types';

const ticketsColumns: CSVColumn[] = [
  {
    key: 'ticket_number',
    header: 'ticket_number',
    required: true,
    exampleValue: 'TKT-1001',
    description:
      'Ticket number as printed. Re-importing the same number updates the existing ticket instead of creating a duplicate.',
  },
  {
    key: 'service_date',
    header: 'service_date',
    required: true,
    exampleValue: '2026-08-10T09:30',
    description:
      'When the load was serviced. Read in the company time zone without an offset.',
  },
  {
    key: 'quantity',
    header: 'quantity',
    required: true,
    exampleValue: '24.5',
    description: 'Delivered quantity',
  },
  {
    key: 'unit_of_measure',
    header: 'unit_of_measure',
    required: true,
    exampleValue: 'Tonne',
    description: 'Unit for the quantity, for example Tonne or Yard',
  },
  {
    key: 'truck_id',
    header: 'truck_id',
    required: true,
    exampleValue: 'TRUCK-17',
    description:
      "Your identifier for the truck; used to match the ticket to a job's truck",
  },
  {
    key: 'truck_description',
    header: 'truck_description',
    required: false,
    exampleValue: 'Tri axle dump',
    description: 'Free-text truck description, used as a fallback for matching',
  },
  {
    key: 'tread_order_id',
    header: 'tread_order_id',
    required: false,
    exampleValue: '',
    description:
      'Tread ID of an existing order to attach the ticket to, skipping matching',
  },
  {
    key: 'dispatch_number',
    header: 'dispatch_number',
    required: false,
    exampleValue: 'D-9',
    description: 'Dispatch number used with the service date to find the order',
  },
  {
    key: 'printed_at',
    header: 'printed_at',
    required: false,
    exampleValue: '2026-08-10T09:25',
    description: 'When the ticket was printed',
  },
  {
    key: 'material_name',
    header: 'material_name',
    required: false,
    exampleValue: '3/4 Crushed Stone',
    description: 'Material name. Created when no match is found.',
  },
  {
    key: 'material_external_id',
    header: 'material_external_id',
    required: false,
    exampleValue: '',
    description: 'Your internal reference ID for the material',
  },
  {
    key: 'customer_name',
    header: 'customer_name',
    required: false,
    exampleValue: 'Acme Paving',
    description: 'Customer account name. Created when no match is found.',
  },
  {
    key: 'customer_external_id',
    header: 'customer_external_id',
    required: false,
    exampleValue: '',
    description: 'Your internal reference ID for the customer',
  },
  {
    key: 'vendor_name',
    header: 'vendor_name',
    required: false,
    exampleValue: '',
    description: 'Vendor name recorded on the ticket for reporting',
  },
  {
    key: 'site_name',
    header: 'site_name',
    required: false,
    exampleValue: 'North Quarry',
    description: 'Site name recorded on the ticket for reporting',
  },
  {
    key: 'phase_name',
    header: 'phase_name',
    required: false,
    exampleValue: 'Base course',
    description: 'Phase name on the matched order; created when not found',
  },
  {
    key: 'phase_code',
    header: 'phase_code',
    required: false,
    exampleValue: 'PH-01',
    description: 'Code of the phase; required when phase_name is set',
  },
];

/**
 * Values pinned onto every row of an upload. A pinned value wins over the row's own,
 * so a per-order upload cannot be redirected to another order by the file's contents.
 */
interface TicketRowDefaults {
  tread_order_id?: string;
}

function buildTicketImportRequest(
  row: Record<string, string>,
  rowDefaults: TicketRowDefaults = {},
): TicketImportRequest {
  const treadOrderId =
    trimmedOrUndefined(rowDefaults.tread_order_id) ??
    trimmedOrUndefined(row.tread_order_id);

  return {
    ticket_number: row.ticket_number?.trim() ?? '',
    service_date: row.service_date?.trim() ?? '',
    quantity: Number(row.quantity?.trim()),
    unit_of_measure: row.unit_of_measure?.trim() ?? '',
    truck_id: row.truck_id?.trim() ?? '',
    ...withoutEmptyValues({
      truck_description: trimmedOrUndefined(row.truck_description),
      tread_order_id: treadOrderId,
      dispatch_number: trimmedOrUndefined(row.dispatch_number),
      printed_at: trimmedOrUndefined(row.printed_at),
      material: withoutEmptyValues({
        name: trimmedOrUndefined(row.material_name),
        external_id: trimmedOrUndefined(row.material_external_id),
      }),
      customer: withoutEmptyValues({
        name: trimmedOrUndefined(row.customer_name),
        external_id: trimmedOrUndefined(row.customer_external_id),
      }),
      vendor: withoutEmptyValues({ name: trimmedOrUndefined(row.vendor_name) }),
      site: withoutEmptyValues({ name: trimmedOrUndefined(row.site_name) }),
      phase: withoutEmptyValues({
        name: trimmedOrUndefined(row.phase_name),
        code: trimmedOrUndefined(row.phase_code),
      }),
    }),
  };
}

async function importTicketRow(
  companyId: string,
  row: Record<string, string>,
  rowDefaults: TicketRowDefaults,
): Promise<ImportedTicket_Read> {
  const response = await postV1CompaniesCompanyIdImportsTickets({
    path: { 'company-id': companyId },
    body: buildTicketImportRequest(row, rowDefaults),
  });

  return response.data.data;
}

function countTicketsWithoutOrder(succeeded: unknown[]) {
  return succeeded.filter((entity) => {
    const importedTicket = entity as Partial<ImportedTicket_Read> | null;

    return !importedTicket?.resolved_order_id;
  }).length;
}

// The shared EntityCSVConfig.update signature only carries the entity id, so the config
// is built per company and closes over the scoped company id for the update path.
export const createTicketsConfig = (
  rowDefaults: TicketRowDefaults = {},
): EntityCSVConfig<ImportedTicket_Read> => ({
  entityType: 'tickets',
  importInstructions: t('csv_import_export.import_instructions.tickets'),
  // Sequential: rows commonly share customers/materials/sites, and imports are upserts
  // by ticket number
  importBatchSize: 1,
  apiFieldToColumn: {
    'Ticket/ticket_number': 'ticket_number',
    'Ticket/service_date': 'service_date',
    'Ticket/quantity': 'quantity',
    'Ticket/unit_of_measure': 'unit_of_measure',
    'Ticket/printed_at': 'printed_at',
    'Material/name': 'material_name',
    'Material/external_id': 'material_external_id',
    'Phase/name': 'phase_name',
    'Phase/code': 'phase_code',
  },
  columns: rowDefaults.tread_order_id
    ? ticketsColumns.filter((column) => column.key !== 'tread_order_id')
    : ticketsColumns,

  create: (companyId: string, row: Record<string, string>) =>
    importTicketRow(companyId, row, rowDefaults),

  transformImport: (row: Record<string, string>): Record<string, unknown> =>
    buildTicketImportRequest(row, rowDefaults),

  summarizeResults: (succeeded: unknown[]) => {
    const unmatchedCount = countTicketsWithoutOrder(succeeded);

    return unmatchedCount > 0
      ? t('csv_import_export.tickets_without_order', { count: unmatchedCount })
      : null;
  },

  rowLabel: (row: Record<string, string>) =>
    trimmedOrUndefined(row.ticket_number) ?? trimmedOrUndefined(row.tread_order_id),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    const requiredFields = ['ticket_number', 'quantity', 'unit_of_measure', 'truck_id'];
    requiredFields.forEach((field) => {
      if (!row[field]?.trim()) {
        errors.push(t('csv_import_export.errors.required_field', { field }));
      }
    });

    if (!row.service_date?.trim()) {
      errors.push(
        t('csv_import_export.errors.required_field', { field: 'service_date' }),
      );
    } else if (!dayjs(row.service_date.trim()).isValid()) {
      errors.push(t('csv_import_export.errors.invalid_date', { field: 'service_date' }));
    }

    if (row.printed_at?.trim() && !dayjs(row.printed_at.trim()).isValid()) {
      errors.push(t('csv_import_export.errors.invalid_date', { field: 'printed_at' }));
    }

    const quantity = row.quantity?.trim();
    if (quantity && isNaN(Number(quantity))) {
      errors.push(t('csv_import_export.errors.invalid_number', { field: 'quantity' }));
    }

    // Phases require both halves — the API rejects a phase without a code
    if (row.phase_name?.trim() && !row.phase_code?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'phase_code' }));
    }
    if (row.phase_code?.trim() && !row.phase_name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'phase_name' }));
    }

    return errors;
  },
});
